import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  CheckIcon, XMarkIcon, ArrowLeftIcon, ChevronDownIcon,
  ShieldCheckIcon, BoltIcon, StarIcon, SparklesIcon,
  EnvelopeIcon, PhoneIcon,
  ChartBarIcon, DocumentTextIcon, BriefcaseIcon,
  WrenchScrewdriverIcon, AcademicCapIcon, ShieldExclamationIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'
import { subscriptionsApi } from '../../services/api'
import { saveSelectedCheckoutPlan } from '../../utils/checkoutPlan'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import RichTextContent from '../../components/common/RichTextContent'
import { RootState } from '../../store/store'
import { evaluatePurchaseAccess } from '../../utils/accessFlow'

interface Plan {
  id: number
  slug: string
  name: string
  description?: string
  price_min: number
  price_max: number
  duration_days: number
  plan_type: string
  billing_period: 'monthly' | 'quarterly' | 'yearly'
  discount_percent: number
  final_price: number
  savings: number
  is_active: boolean
  highlight_badge?: string | null
  sort_order?: number
  core_details?: string
  email_delivery: boolean
  telegram_delivery: boolean
  whatsapp_support: boolean
  priority_support: boolean
  monthly_outlook: boolean
  weekly_sectoral_reports: boolean
  custom_research_reports: boolean
  market_radar_newsletter: boolean
  intraday_trades: boolean
  positional_trades: boolean
  strategy_based_options: boolean
  portfolio_tracker?: string
  portfolio_audit_frequency?: string
  portfolio_rebalancing: boolean
  risk_management_plan: boolean
  watchlist_access: boolean
  alerts_access: boolean
  risk_meter_access: boolean
  live_market_access: boolean
  review_calls?: string
  call_support: boolean
  compliance_dashboard: boolean
  net_worth_certificate: boolean
  basic_education_content: boolean
}

interface PlanTheme {
  border: string
  accent: string
  iconBg: string
  chip: string
  checkChip: string
  cta: string
  badge: string
  glowBg: string
  priceGradient: string
  tabActive: string
}

const PLAN_THEME: Record<string, PlanTheme> = {
  BASIC: {
    border: 'border-emerald-500/40',
    accent: 'text-emerald-400',
    iconBg: 'bg-emerald-500/10',
    chip: 'bg-emerald-500/15 text-emerald-300',
    checkChip: 'bg-emerald-500/15 text-emerald-400',
    cta: 'bg-emerald-500 hover:bg-emerald-400 text-black',
    badge: 'bg-emerald-500 text-black',
    glowBg: 'bg-emerald-500',
    priceGradient: 'from-emerald-300 to-emerald-500',
    tabActive: 'bg-emerald-500 text-black border-emerald-500',
  },
  STANDARD: {
    border: 'border-amber-500/50',
    accent: 'text-amber-400',
    iconBg: 'bg-amber-500/10',
    chip: 'bg-amber-500/15 text-amber-300',
    checkChip: 'bg-amber-500/15 text-amber-400',
    cta: 'bg-amber-500 hover:bg-amber-400 text-black',
    badge: 'bg-amber-400 text-black',
    glowBg: 'bg-amber-400',
    priceGradient: 'from-amber-300 to-amber-500',
    tabActive: 'bg-amber-500 text-black border-amber-500',
  },
  PREMIUM: {
    border: 'border-purple-500/40',
    accent: 'text-purple-400',
    iconBg: 'bg-purple-500/10',
    chip: 'bg-purple-500/15 text-purple-300',
    checkChip: 'bg-purple-500/15 text-purple-400',
    cta: 'bg-purple-500 hover:bg-purple-400 text-white',
    badge: 'bg-purple-500 text-white',
    glowBg: 'bg-purple-500',
    priceGradient: 'from-purple-300 to-purple-500',
    tabActive: 'bg-purple-500 text-white border-purple-500',
  },
  VIP: {
    border: 'border-rose-500/40',
    accent: 'text-rose-400',
    iconBg: 'bg-rose-500/10',
    chip: 'bg-rose-500/15 text-rose-300',
    checkChip: 'bg-rose-500/15 text-rose-400',
    cta: 'bg-rose-500 hover:bg-rose-400 text-white',
    badge: 'bg-rose-500 text-white',
    glowBg: 'bg-rose-500',
    priceGradient: 'from-rose-300 to-rose-500',
    tabActive: 'bg-rose-500 text-white border-rose-500',
  },
}

const PLAN_ICON: Record<string, React.ReactNode> = {
  BASIC: <ShieldCheckIcon className="w-9 h-9" />,
  STANDARD: <BoltIcon className="w-9 h-9" />,
  PREMIUM: <StarIcon className="w-9 h-9" />,
  VIP: <SparklesIcon className="w-9 h-9" />,
}

const PLAN_LABEL: Record<string, string> = {
  BASIC: 'STARTER',
  STANDARD: 'PRO RESEARCHER',
  PREMIUM: 'ELITE HNI',
  VIP: 'VIP',
}

type Feature = { label: string; value: boolean | string }

function section(title: string, icon: React.ReactNode, items: Feature[]) {
  const hasAny = items.some(i => i.value)
  return { title, icon, items, hasAny }
}

function buildSections(plan: Plan) {
  return [
    section('Delivery Channels', <EnvelopeIcon className="w-5 h-5" />, [
      { label: 'Email delivery', value: plan.email_delivery },
      { label: 'Telegram alerts', value: plan.telegram_delivery },
      { label: 'WhatsApp support', value: plan.whatsapp_support },
      { label: 'Priority support', value: plan.priority_support },
    ]),
    section('Research Reports', <DocumentTextIcon className="w-5 h-5" />, [
      { label: 'Monthly market outlook', value: plan.monthly_outlook },
      { label: 'Weekly sectoral reports', value: plan.weekly_sectoral_reports },
      { label: 'Custom research reports', value: plan.custom_research_reports },
      { label: 'Market radar newsletter', value: plan.market_radar_newsletter },
    ]),
    section('Trading Coverage', <ChartBarIcon className="w-5 h-5" />, [
      { label: 'Intraday calls', value: plan.intraday_trades },
      { label: 'Positional calls', value: plan.positional_trades },
      { label: 'Strategy-based F&O', value: plan.strategy_based_options },
    ]),
    section('Portfolio Services', <BriefcaseIcon className="w-5 h-5" />, [
      ...(plan.portfolio_tracker
        ? [{ label: `${plan.portfolio_tracker === 'premium' ? 'Premium' : 'Basic'} portfolio tracker`, value: true }]
        : [{ label: 'Portfolio tracker', value: false }]),
      ...(plan.portfolio_audit_frequency
        ? [{ label: `${plan.portfolio_audit_frequency === 'monthly' ? 'Monthly' : 'Quarterly'} portfolio audit`, value: true }]
        : [{ label: 'Portfolio audit', value: false }]),
      { label: 'Portfolio rebalancing advisory', value: plan.portfolio_rebalancing },
      { label: 'Risk management plan', value: plan.risk_management_plan },
    ]),
    section('Platform Tools', <WrenchScrewdriverIcon className="w-5 h-5" />, [
      { label: 'Watchlist access', value: plan.watchlist_access },
      { label: 'Price alerts', value: plan.alerts_access },
      { label: 'Risk meter', value: plan.risk_meter_access },
      { label: 'Live market access', value: plan.live_market_access },
    ]),
    section('Support & Advisory', <PhoneIcon className="w-5 h-5" />, [
      ...(plan.review_calls
        ? [{ label: `${plan.review_calls} review calls`, value: true }]
        : [{ label: 'Review calls', value: false }]),
      { label: 'Call support', value: plan.call_support },
    ]),
    section('Compliance & Education', <AcademicCapIcon className="w-5 h-5" />, [
      { label: 'Compliance dashboard', value: plan.compliance_dashboard },
      { label: 'Net worth certificate', value: plan.net_worth_certificate },
      { label: 'Education content library', value: plan.basic_education_content },
    ]),
  ]
}

function supportLevel(plan: Plan): string {
  if (plan.priority_support) return 'Priority'
  if (plan.call_support) return 'Call support'
  if (plan.whatsapp_support) return 'WhatsApp'
  return 'Standard'
}

export default function PlanDetailsPage() {
  const { slug, planRef } = useParams<{ slug?: string; planRef?: string }>()
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  const [plan, setPlan] = useState<Plan | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [openIndex, setOpenIndex] = useState<number>(0)

  useEffect(() => {
    if (!slug && !planRef) { navigate('/pricing'); return }

    const load = async () => {
      try {
        const resolvedSlug = slug || (planRef && !/^\d+$/.test(planRef) ? planRef : undefined)

        if (resolvedSlug) {
          const result = await subscriptionsApi.getPlanBySlug(resolvedSlug)
          setPlan(result)
          return
        }

        const numericId = Number(planRef || '')
        if (!Number.isFinite(numericId) || numericId <= 0) {
          setError('Plan not found')
          return
        }

        // Backward compatibility: old numeric links resolve and then rewrite to slug URL.
        const legacyPlan = await subscriptionsApi.getPlan(numericId)
        setPlan(legacyPlan)
        navigate(`/plans/${legacyPlan.slug}`, { replace: true })
      } catch {
        setError('Plan not found')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [slug, planRef, navigate])

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !plan) {
    return (
      <div className="min-h-screen bg-dark-900 flex flex-col items-center justify-center gap-4">
        <ShieldExclamationIcon className="w-12 h-12 text-gray-400" aria-hidden="true" focusable="false" />
        <p className="text-gray-300">Plan not found.</p>
        <Link to="/pricing" className="text-primary-400 hover:underline text-sm">← Back to all plans</Link>
      </div>
    )
  }

  const theme = PLAN_THEME[plan.plan_type] || PLAN_THEME.BASIC
  const sections = buildSections(plan)
  const toggle = (i: number) => setOpenIndex(prev => (prev === i ? -1 : i))
  const dynamicFeatures: {title: string, value: string}[] = []
  try {
    if (plan.core_details) {
      const parsed = JSON.parse(plan.core_details)
      if (Array.isArray(parsed)) {
        parsed.forEach(p => dynamicFeatures.push(p))
      }
    }
  } catch {}

  const stats = [
    { label: 'Plan Duration', value: `${plan.duration_days} days` },
    { label: 'Key Feature', value: dynamicFeatures.length > 0 ? dynamicFeatures[0].value : 'Custom' },
    { label: 'Support Level', value: supportLevel(plan) },
  ]

  const billingCycle = plan.duration_days === 30 ? 'Billed monthly'
    : plan.duration_days === 90 ? 'Billed quarterly'
    : plan.duration_days === 365 ? 'Billed yearly'
    : `Billed every ${plan.duration_days} days`
  const savings = Number(plan.savings) || 0
  const finalPrice = Number(plan.final_price ?? plan.price_min)
  const listPrice = Number(plan.price_min)

  // Key highlights surfaced in the price card
  const cardHighlights = [
    ...dynamicFeatures.map(f => f.value),
    plan.priority_support ? 'Priority support' : plan.whatsapp_support ? 'WhatsApp support' : null,
    plan.custom_research_reports ? 'Custom research reports' : plan.monthly_outlook ? 'Monthly market outlook' : null,
  ].filter(Boolean).slice(0, 5) as string[]

  return (
    <div className="min-h-screen bg-dark-900 text-white pb-28 md:pb-0">

      {/* Theme glow backdrop anchored to the top of the page.
          NOTE: overflow-hidden lives on this glow layer (a sibling), NOT on the
          wrapper — an overflow-hidden ancestor would break the sticky price card. */}
      <div className="relative">
        <div className="absolute inset-x-0 top-0 h-[520px] overflow-hidden pointer-events-none" aria-hidden="true">
          <div className={`absolute -top-24 -right-16 w-96 h-96 rounded-full blur-[90px] opacity-[0.15] ${theme.glowBg}`} />
          <div className={`absolute top-40 -left-16 w-80 h-80 rounded-full blur-[90px] opacity-[0.08] ${theme.glowBg}`} />
        </div>

        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-5 sm:pt-6 pb-8">
          {/* Breadcrumb */}
          <div className="inline-flex items-center gap-2 text-xs text-gray-400 mb-5">
            <Link to="/" className="hover:text-gray-300 transition-colors">Home</Link>
            <span aria-hidden="true">/</span>
            <Link to="/pricing" className="hover:text-gray-300 transition-colors">Pricing</Link>
            <span aria-hidden="true">/</span>
            <span className="text-gray-300" aria-current="page">{plan.name}</span>
          </div>

          {/* Two-column: left = all content, right = always-visible price card */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 lg:gap-8 items-start">

            {/* Identity — left column, row 1 */}
            <div className="min-w-0 lg:col-start-1 lg:row-start-1">
              <div className="flex flex-wrap items-center gap-3 mb-3.5">
                <div className={`w-11 h-11 rounded-xl border ${theme.border} ${theme.iconBg} ${theme.accent} flex items-center justify-center flex-shrink-0`} aria-hidden="true">
                  {PLAN_ICON[plan.plan_type]}
                </div>
                <span className={`inline-flex w-fit text-[11px] font-bold tracking-widest px-2.5 py-1 rounded-full ${theme.chip}`}>
                  {PLAN_LABEL[plan.plan_type] || plan.plan_type}
                </span>
                {plan.highlight_badge && (
                  <span className={`inline-flex w-fit items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full ${theme.badge}`}>
                    <StarSolidIcon className="w-3 h-3" aria-hidden="true" />
                    {plan.highlight_badge}
                  </span>
                )}
              </div>

              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight text-white mb-2">{plan.name}</h1>
              {plan.description && (
                <RichTextContent html={plan.description} className="text-sm text-gray-400 leading-6 max-w-xl mb-4" />
              )}

              {/* Stat pills */}
              <div className="flex flex-wrap gap-2">
                {stats.map(stat => (
                  <div
                    key={stat.label}
                    className="inline-flex items-baseline gap-1.5 rounded-full border border-dark-700 bg-dark-800/70 pl-3 pr-3 py-1.5 max-w-full"
                    title={stat.value}
                  >
                    <span className={`text-xs font-bold truncate max-w-[12rem] ${theme.accent}`}>{stat.value}</span>
                    <span className="text-[11px] text-gray-500 whitespace-nowrap">· {stat.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Price card — right column, spans full height, always visible (sticky).
                lg:self-stretch overrides the grid's items-start so this wrapper fills
                the full 2-row height, giving the inner sticky card room to travel. */}
            <div className="w-full lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-stretch">
              <div className={`pricing-card-container ${plan.plan_type.toLowerCase()}-card bg-dark-800/90 backdrop-blur rounded-2xl border-2 p-5 lg:sticky lg:top-6 ${theme.border} transition-all duration-300`}>
                {/* Plan name + badge */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg font-bold text-white">
                    {plan.name}
                  </span>
                  {plan.highlight_badge && (
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${theme.badge}`}>
                      <StarSolidIcon className="w-2.5 h-2.5" aria-hidden="true" />
                      {plan.highlight_badge}
                    </span>
                  )}
                </div>

                {/* Price */}
                <div className="pricing-card-price-block flex items-end gap-2 flex-wrap">
                  <span className={`text-4xl font-bold leading-none bg-gradient-to-r ${theme.priceGradient} bg-clip-text text-transparent`}>
                    ₹{finalPrice.toLocaleString('en-IN')}
                  </span>
                  {savings > 0 && (
                    <span className="text-gray-500 text-sm line-through">₹{listPrice.toLocaleString('en-IN')}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <p className="text-xs text-gray-400">for {plan.duration_days} days · {billingCycle}</p>
                </div>
                {savings > 0 && (
                  <span className={`inline-flex mt-2.5 text-[11px] font-bold px-2.5 py-1 rounded-full ${theme.checkChip}`}>
                    Save ₹{savings.toLocaleString('en-IN')}
                  </span>
                )}

                {/* CTA */}
                <button
                  type="button"
                  onClick={async () => {
                    saveSelectedCheckoutPlan(plan.slug)
                    const next = `/dashboard/checkout?planSlug=${plan.slug}`
                    const access = await evaluatePurchaseAccess(user, { next })
                    navigate(access.allowed ? next : (access.redirectTo || '/login'))
                  }}
                  className={`block w-full py-3 rounded-xl text-center font-bold text-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg mt-4 ${theme.cta}`}
                >
                  Get Started
                </button>
                <Link
                  to="/pricing"
                  className="flex items-center justify-center gap-1.5 mt-3 text-xs text-gray-400 hover:text-gray-300 transition-colors"
                >
                  <ArrowLeftIcon className="w-3.5 h-3.5" aria-hidden="true" focusable="false" />
                  View all plans
                </Link>

                {/* What you get — key highlights */}
                {cardHighlights.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-dark-700">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2.5">What you get</p>
                    <ul className="space-y-2">
                      {cardHighlights.map(h => (
                        <li key={h} className="flex items-start gap-2 text-xs text-gray-300 leading-5">
                          <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${theme.checkChip}`}>
                            <CheckIcon className="w-2.5 h-2.5" aria-hidden="true" focusable="false" />
                          </span>
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Reassurance row */}
                <div className="mt-4 pt-4 border-t border-dark-700 grid grid-cols-2 gap-2 text-center">
                  <div>
                    <p className={`text-xs font-bold ${theme.accent}`}>Fixed fee</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">No commissions</p>
                  </div>
                  <div>
                    <p className={`text-xs font-bold ${theme.accent}`}>No execution</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">You stay in control</p>
                  </div>
                </div>

                <p className="text-[10px] text-gray-500 mt-4 leading-4 text-center">
                  SEBI Reg. INH000023719 · Advisory services only
                </p>
              </div>
            </div>

            {/* All content — left column, row 2 (flows under identity, beside sticky card) */}
            <div className="min-w-0 lg:col-start-1 lg:row-start-2">
              <div className="mb-4">
                <h2 className="text-xl lg:text-2xl font-bold text-white leading-tight">What's included</h2>
                <p className="text-xs text-gray-400 leading-5 mt-0.5">Browse every feature category in this plan</p>
              </div>

              {/* Accordion — categories stacked back-to-back */}
              <div className="space-y-2.5">
                {sections.map((sec, i) => {
                  const open = i === openIndex
                  const panelId = `feature-panel-${i}`
                  const buttonId = `feature-tab-${i}`
                  return (
                    <div
                      key={sec.title}
                      className={`rounded-2xl border overflow-hidden transition-colors duration-200 ${open ? theme.border : 'border-dark-700'} bg-dark-800`}
                    >
                      <button
                        id={buttonId}
                        type="button"
                        aria-expanded={open}
                        aria-controls={panelId}
                        onClick={() => toggle(i)}
                        className="w-full flex items-center gap-2.5 px-5 py-4 text-left transition-colors hover:bg-dark-700/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                      >
                        <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${theme.iconBg} ${theme.accent}`} aria-hidden="true">
                          {sec.icon}
                        </span>
                        <h3 className="font-semibold text-white text-sm sm:text-base">{sec.title}</h3>
                        {!sec.hasAny && (
                          <span className="text-[11px] text-gray-500 whitespace-nowrap px-2 py-0.5 rounded-full bg-dark-700/70">Not included</span>
                        )}
                        <ChevronDownIcon
                          className={`w-5 h-5 text-gray-400 flex-shrink-0 ml-auto transition-transform duration-300 ${open ? `rotate-180 ${theme.accent}` : ''}`}
                          aria-hidden="true"
                          focusable="false"
                        />
                      </button>

                      {/* Animated collapsible region (grid-rows trick for smooth height) */}
                      <div
                        id={panelId}
                        role="region"
                        aria-labelledby={buttonId}
                        className={`grid transition-all duration-300 ease-in-out ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                      >
                        <div className="overflow-hidden">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 px-5 pb-5 pt-0.5">
                            {sec.items.map((item) => (
                              <div key={item.label} className="flex items-start gap-2.5 min-w-0">
                                {item.value ? (
                                  <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${theme.checkChip}`}>
                                    <CheckIcon className="w-3 h-3" aria-hidden="true" focusable="false" />
                                  </span>
                                ) : (
                                  <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-dark-700">
                                    <XMarkIcon className="w-3 h-3 text-gray-500" aria-hidden="true" focusable="false" />
                                  </span>
                                )}
                                <span className={`text-sm leading-6 ${item.value ? 'text-gray-200' : 'text-gray-500'}`}>
                                  <span className="sr-only">{item.value ? 'Included: ' : 'Not included: '}</span>
                                  {item.label}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Compare plans CTA */}
              <div className={`mt-4 bg-dark-800 border rounded-2xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl ${theme.border}`}>
                <div className="flex-1">
                  <p className="font-semibold text-white text-sm leading-5">Not sure this plan is right for you?</p>
                  <p className="text-xs text-gray-400 mt-0.5 leading-5">Compare all available plans side by side.</p>
                </div>
                <Link
                  to="/pricing"
                  className="px-4 py-2 rounded-xl border border-dark-600 bg-dark-700 text-sm font-medium text-gray-300 hover:bg-dark-600 transition-colors flex-shrink-0"
                >
                  Compare plans
                </Link>
              </div>

              {/* Trust strip */}
              <div className="mt-4 bg-dark-800 border border-dark-700 rounded-2xl px-5 py-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center text-sm">
                  <div>
                    <p className="font-semibold text-white text-sm">SEBI Registered</p>
                    <p className="text-gray-400 text-xs mt-0.5">Research Analyst</p>
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">Fixed Fee Model</p>
                    <p className="text-gray-400 text-xs mt-0.5">No profit-sharing or commissions</p>
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">No Execution Access</p>
                    <p className="text-gray-400 text-xs mt-0.5">We advise, you decide</p>
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">Independent Research</p>
                    <p className="text-gray-400 text-xs mt-0.5">Conflict-free recommendations</p>
                  </div>
                </div>
              </div>

              {/* Regulatory disclaimer */}
              <p className="text-[11px] text-gray-500 leading-5 mt-4">
                AnalyzerView (SEBI Reg. INH000023719, BSE RAASB Enlistment No. 7041) provides fixed-fee research advisory services.
                All recommendations are for informational purposes only and do not constitute investment advice.
                Subscription fees are non-refundable once services are delivered. Past performance is not indicative of future returns.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky CTA bar */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-dark-700 bg-dark-900/95 backdrop-blur px-4 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] text-gray-400 leading-none">Starting at</p>
          <p className={`text-xl font-bold leading-tight truncate bg-gradient-to-r ${theme.priceGradient} bg-clip-text text-transparent`}>
            ₹{Number(plan.price_min).toLocaleString('en-IN')}
            <span className="text-xs text-gray-400 font-normal"> / {plan.duration_days}d</span>
          </p>
        </div>
        <button
          type="button"
          onClick={async () => {
            saveSelectedCheckoutPlan(plan.slug)
            const next = `/dashboard/checkout?planSlug=${plan.slug}`
            const access = await evaluatePurchaseAccess(user, { next })
            navigate(access.allowed ? next : (access.redirectTo || '/login'))
          }}
          className={`px-6 py-3 rounded-xl font-bold text-sm whitespace-nowrap transition-all duration-300 ${theme.cta}`}
        >
          Get Started
        </button>
      </div>
    </div>
  )
}
